

import { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "@fmc-digital/i18n";
import { RegisterContext } from "@profile/contexts/RegisterContext";
import { MFACodeContainer } from "../../../components/organisms/MFACodeContainer/MFACodeContainer";
import { triggerOtp } from "@fmc-digital/auth-api-generated/src/api/fmc/unauth/customer/triggerOtp";
import { validateOtp } from "@fmc-digital/auth-api-generated/src/api/fmc/unauth/customer/validateOtp";
import type { RegisterTriggerOTPRequest } from "@fmc-digital/auth-api-generated/types/fmc/unauth";
import {
  extractApiErrorCode,
  getRegisterErrorMessageFromCode,
  handleRegisterOtpValidationError,
  type RegisterOtpValidationResult,
} from "@profile/features/registration/registerErrorMessage";
import { RegistrationEvent } from "@profile/features/registration/analytics";
import { useRegistrationProvider } from "@profile/providers/RegistrationProvider";

interface OtpStepProps {
  setStatusMessage: (msg: string | null) => void;
  enableResendOnLoad?: boolean;
  onConsumeEnableResendOnLoad?: () => void;
}

export function OtpStep({
  setStatusMessage,
  enableResendOnLoad,
  onConsumeEnableResendOnLoad,
}: OtpStepProps) {
  const { t, i18n } = useTranslation();
  const { config, trackRegistrationEvent } = useRegistrationProvider();
  const { setOtp, email, setStep, setEmailSubstep, otpId, otpFactorId, setOtpId, setOtpFactorId, goBack } = useContext(RegisterContext);
  const [otpError, setOtpError] = useState<string | null>(null);
  const translate = useCallback((key: string) => String(t(key as never)), [t]);
  const i18nExists = useCallback((key: string) => i18n.exists(key), [i18n]);

  const factorState = useMemo(
    () => ({
      factorType: "email" as const,
      profile: { email },
    }),
    [email],
  );

  const trackEmailOtpValidationFailed = useCallback((
    result: RegisterOtpValidationResult | undefined,
    errorCode: string | null,
    description?: string | null,
  ) => {
    if (
      result?.status === "CLEAR_CODE" ||
      result?.status === "ALREADY_VALIDATED" ||
      result?.status === "BANNER_ONLY"
    ) {
      return;
    }
    try {
      trackRegistrationEvent(RegistrationEvent.EMAIL_OTP_VALIDATION_FAILED, {
        ...(errorCode ? { errorCode } : {}),
        ...(description ? { description } : {}),
      });
    } catch {
      // Analytics must not block flow
    }
  }, [trackRegistrationEvent]);

  const handleValidate = useCallback(async (code: string) => {
    setStatusMessage(null);
    setOtpError(null);
    if (!otpId || !otpFactorId) {
      setOtpError("Something went wrong.");
      return null;
    }
    try {
      trackRegistrationEvent(RegistrationEvent.EMAIL_OTP_SUBMITTED);
    } catch {
      // Analytics must not block validation
    }
    try {
      return await validateOtp({
        factorValue: code,
        id: otpId,
        factorId: otpFactorId,
      });
    } catch (err) {
      const errorCode = extractApiErrorCode(err);
      const message = getRegisterErrorMessageFromCode(
        err,
        translate,
        i18nExists,
      );

      const result = handleRegisterOtpValidationError(
        errorCode,
        message,
        setStatusMessage,
        setOtpError,
      );
      trackEmailOtpValidationFailed(result, errorCode, message);
      return result;
    }
  }, [
    i18nExists,
    otpFactorId,
    otpId,
    setStatusMessage,
    trackEmailOtpValidationFailed,
    trackRegistrationEvent,
    translate,
  ]);

  const handleVerify = useCallback((verifiedCode: string) => {
    setStatusMessage(null);
    try {
      trackRegistrationEvent(RegistrationEvent.EMAIL_OTP_VALIDATION_SUCCESS);
    } catch {
      // Analytics must not block navigation
    }
    setOtp(verifiedCode);
    setStep("personalInfo");
    setEmailSubstep("input");
  }, [setEmailSubstep, setOtp, setStatusMessage, setStep, trackRegistrationEvent]);

  const handleInputEdit = useCallback(() => {
    if (otpError) setOtpError(null);
    setStatusMessage(null);
  }, [otpError, setStatusMessage]);

  const handleCancel = useCallback(() => {
    setOtpError(null);
    setStatusMessage(null);
    goBack();
  }, [goBack, setStatusMessage]);

  const handleResend = useCallback(async () => {
    setStatusMessage(null);
    let prevBaseUrl: string | undefined = undefined;

    try {
      const baseUrlV2 = config.apiBaseUrlV2 || config.apiBaseUrl;

      if (typeof window !== "undefined" && window.__authShellConfig) {
        prevBaseUrl = window.__authShellConfig.apiBaseUrl;
        window.__authShellConfig.apiBaseUrl = `${baseUrlV2}/unauth`;
      }

      const payload: RegisterTriggerOTPRequest = {
        factor: {
          factorType: "EMAIL",
          email,
        },
      };

      const response = await triggerOtp(payload);

      if (response?.id) {
        setOtpId(response.id);
        setOtpFactorId(response.factorId || "");
      } else {
        throw new Error("register:otp_trigger_failed");
      }
    } catch (err) {
      const message = getRegisterErrorMessageFromCode(
        err,
        translate,
        i18nExists,
      );
      setStatusMessage(message);
      throw err;
    } finally {
      if (typeof window !== "undefined" && window.__authShellConfig && prevBaseUrl !== undefined) {
        window.__authShellConfig.apiBaseUrl = prevBaseUrl;
      }
    }
  }, [config, email, i18nExists, setOtpFactorId, setOtpId, setStatusMessage, translate]);

  return (
    <MFACodeContainer
      factorState={factorState}
      onVerify={handleVerify}
      onCancel={handleCancel}
      onValidate={handleValidate}
      onResend={handleResend}
      inputError={otpError}
      onInputEdit={handleInputEdit}
      enableResendOnLoad={enableResendOnLoad}
      onConsumeEnableResendOnLoad={onConsumeEnableResendOnLoad}
    />
  );
}
